import React, { useEffect, useState } from "react";
import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { closeModal } from "store/modals/slice";
import {
  getPaymentItem,
  handleClearDraftItem,
  updatePaymentItem,
  createPaymentItem,
} from "store/cms/footer/payments/slice";
import { getPaymentMethodsList } from "store/payments/methods/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalPaymentsTabs, ModalTypes } from "configs/types";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import styles from "../payments/CmsFooterPaymentsEditModal.module.scss";

import PaymentsMainTab from "./tabs/PaymentsMainTab";
import PaymentsTrailTab from "./tabs/PaymentsTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const CmsFooterPaymentsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  const geoList = useAppSelector(selectFilteredGeo);
  const isItemFetched = useAppSelector(
    (store) => store["cms-payments"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-payments"].isItemFetching,
  );
  const draftItem = useAppSelector((store) => store["cms-payments"].draftItem);
  const paymentMethodsListGlobal = useAppSelector(
    (store) => store["payment-methods"].list,
  );

  const [activeTab, setActiveTab] = useState<ModalPaymentsTabs>(
    ModalPaymentsTabs.main,
  );

  const tabs = [ModalPaymentsTabs.main, ModalPaymentsTabs.trail];

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getPaymentItem(+entityId ? +entityId : 0));
    }
  }, [isItemFetched, searchParams]);

  useEffect(() => {
    if (!paymentMethodsListGlobal.length) {
      dispatch(
        getPaymentMethodsList({
          limit: 20,
          offset: 0,
          title: "",
          providerTitle: "",
          transactionType: "",
          order: {},
        }),
      );
    }
  }, [paymentMethodsListGlobal]);

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const canBeSaved = () => {
    return draftItem?.isModified || false;
  };

  const handleSave = async () => {
    const { id, title, isActive, geo, payments } = draftItem;
    const paymentIds = payments.map((payment) => payment.id);

    formErrors.resetErrors();

    try {
      if (!id) {
        const result = await dispatch(
          createPaymentItem({
            title,
            isActive: isActive ? 1 : 0,
            country: geo || geoList[0],
            paymentIds,
          }),
        ).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.payments}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        return;
      }

      await dispatch(
        updatePaymentItem({
          id,
          title,
          isActive: isActive ? 1 : 0,
          country: geo,
          paymentIds,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const isLoaded = () => {
    if (entityId) {
      return isItemFetched && paymentMethodsListGlobal.length > 0;
    }
    return paymentMethodsListGlobal.length > 0;
  };

  const loaded = isLoaded();

  const handleTabClick = (tab: ModalPaymentsTabs) => {
    if (!isEdit && tab !== ModalPaymentsTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalPaymentsTabs.main) {
      return (
        <PaymentsMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalPaymentsTabs.trail) {
      if (!entityId) return null;

      return <PaymentsTrailTab entityId={entityId} />;
    }

    return null;
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("payments")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content className={styles.cmsFooterPaymentsWrapper}>
            {renderTab()}
          </Modal.Content>

          {activeTab !== ModalPaymentsTabs.trail && permissions?.update && (
            <Modal.Footer>
            <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
            <Button
              isPrimary
              handleClick={handleSave}
              txt={t("save")}
              isLoad={isItemFetching}
              disabled={!canBeSaved()}
            />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsFooterPaymentsEditModal;
